import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as authApi from '../api/auth.js'

const AuthContext = createContext(null)
const ME_KEY = ['auth', 'me']

export function AuthProvider({ children }) {
  const queryClient = useQueryClient()

  const { data, isLoading, isFetched, refetch } = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await authApi.fetchCurrentUser()
      } catch {
        return null
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const login = useCallback(
    async (email, password) => {
      const user = await authApi.login({ email, password })
      queryClient.setQueryData(ME_KEY, user)
      return user
    },
    [queryClient],
  )

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      queryClient.setQueryData(ME_KEY, null)
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' })
    }
  }, [queryClient])

  useEffect(() => {
    const handler = () => {
      queryClient.setQueryData(ME_KEY, null)
    }
    window.addEventListener('arrow:unauthorized', handler)
    return () => window.removeEventListener('arrow:unauthorized', handler)
  }, [queryClient])

  const value = useMemo(
    () => ({
      user: data ?? null,
      isLoading: isLoading && !isFetched,
      isAuthenticated: Boolean(data),
      login,
      logout,
      refetch,
    }),
    [data, isLoading, isFetched, login, logout, refetch],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
